import classNames from 'classnames'
import { useDispatch, useSelector } from 'react-redux'
import { useSearchParams } from 'react-router-dom'
import { useContext, useState } from 'react'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'

import { AccountContext } from '../AccountContext'
import LoadingSpinner from '../../common/loader/legacy-loaders/LoadingSpinner'
import NotificationToggle from './NotificationToggle'

import classes from './NotificationsForm.module.scss'
import { ReactComponent as InfoCircleIcon } from '../../../assets/images/common/icons/info-circle-icon-dark.svg'
import { flatServerFormErrors } from '../../../utility/helperFunctions'

const NotificationsForm = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const currentUser = useSelector(state => state.user.current_user)
  const { user, updateAction } = useContext(AccountContext) // Note: user could be golfer OR current_user
  const [, setSearchParams] = useSearchParams()

  const [isUpdating, setIsUpdating] = useState(false)
  const [, setErrors] = useState({})

  const isCurrentUser = currentUser.id === user.id
  const isCoach = currentUser.type === 'Coach'
  const hasAccess = isCurrentUser && isCoach

  const disabledEmails = user.user_settings.disabled_emails

  const getDefaultValue = (fieldName) => !disabledEmails.find(email => email === fieldName)

  const handleToggleNotification = async (e) => {
    const updatedDisabledEmails =
      e.target.checked
        ? disabledEmails.filter(email => email !== e.target.name)
        : [...disabledEmails, e.target.name]

    const partialUser = {
      id: user.id,
      ...(user.user_settings.id && { 'user_setting_attributes[id]': user.user_settings.id }),
      disabled_emails: updatedDisabledEmails,
    }

    try {
      setIsUpdating(true)
      const newState = await dispatch(updateAction(partialUser))

      if (newState.type === 'UI_ERROR') {
        setErrors(flatServerFormErrors(newState?.error))
      } else {
        toast.success(t('account.notifications.updated'))
      }
    } catch {
      toast.error(t('account.common.userUpdateFailed'))
    } finally {
      setIsUpdating(false)
    }
  }

  if (!hasAccess) {
    setSearchParams({ editTab: 'personal' })
    return null
  }

  return (
    <form onInput={handleToggleNotification} className={classNames(classes.container, 'animated fadeIn')}>
      <div className={classNames(classes.header, 'row d-flex align-items-center mb-4')}>
        <h4 className={classes.title}>{t('account.notifications.title')}</h4>
        <InfoCircleIcon role='button' width={20} height={20} />
        {isUpdating && (
          <LoadingSpinner classNames={{ wrapper: classNames(classes.loading, 'mt-0'), spinner: 'fa-lg' }} />
        )}
      </div>

      <div className={classNames(classes.subHeader, 'd-flex flex-column row')}>
        <h5 className='font-weight-bold mb-1'>{t('account.notifications.emailNotifications')}</h5>
        <p>{t('account.notifications.emailNotificationsDescription')}</p>
      </div>

      <NotificationToggle
        id='enrollmentRequests'
        name='enrollment_request'
        defaultChecked={getDefaultValue('enrollment_request')}
        title={t('account.notifications.toggles.enrollmentRequest.title')}
        description={t('account.notifications.toggles.enrollmentRequest.description')}
        disabled={isUpdating}
      />
      <NotificationToggle
        id='onlineRegistrations'
        name='online_registration'
        defaultChecked={getDefaultValue('online_registration')}
        title={t('account.notifications.toggles.onlineRegistration.title')}
        description={t('account.notifications.toggles.onlineRegistration.description')}
        disabled={isUpdating}
      />
      <NotificationToggle
        id='programFull'
        name='program_full'
        defaultChecked={getDefaultValue('program_full')}
        title={t('account.notifications.toggles.programFull.title')}
        description={t('account.notifications.toggles.programFull.description')}
        disabled={isUpdating}
      />
      <NotificationToggle
        id='familyAccountCreated'
        name='family_account_created'
        defaultChecked={getDefaultValue('family_account_created')}
        title={t('account.notifications.toggles.familyAccountCreated.title')}
        description={t('account.notifications.toggles.familyAccountCreated.description')}
        disabled={isUpdating}
      />
      <NotificationToggle
        id='golferAccountCreated'
        name='golfer_account_created'
        defaultChecked={getDefaultValue('golfer_account_created')}
        title={t('account.notifications.toggles.golferAccountCreated.title')}
        description={t('account.notifications.toggles.golferAccountCreated.description')}
        disabled={isUpdating}
      />
      <NotificationToggle
        id='curriculum'
        name='curriculum'
        defaultChecked={getDefaultValue('curriculum')}
        title={t('account.notifications.toggles.curriculum.title')}
        description={t('account.notifications.toggles.curriculum.description')}
        disabled={isUpdating}
      />
    </form>
  )
}

export default NotificationsForm
